// ГЛОССАРИЙ СЛОЁВ И ЛИСТ `?` ПАНЕЛИ A (ШАГ 3) — дословный корпус. B5-3.
//
// ИСТОЧНИК — `GLOSS` борда `Life Map - s3 Deck v8.html` (`:373–380`) и сборка листа `sheetLayer()`
// (`:637–640`). Определения слоёв в борде помечены `verbatim from Layer glossary · Catalog
// s175–s179 (R10 §V GLOSS)`, то есть борд сам объявляет их ЦИТАТОЙ из каталога, а не своей копи.
// Сверка машинная — `src/render/__parity__/wizard-s3-copy.test.js`.
//
// ⛔ ПОДПИСЬ `layer glossary · catalog s175–s179` (борд `:640`) НА ЭКРАН НЕ ИДЁТ — §19 спеки, слово
// Кирилла. Её здесь НЕТ ВОВСЕ, а не «есть, но не печатается»: строка, лежащая в корпусе, рано или
// поздно доезжает до экрана. Отрицательное утверждение про неё стоит в парити-тесте.
//
// ⚠️ ПОЧЕМУ НЕ В `deckCopy.js`. Тот файл сам объявил, что глоссария в нём нет и почему (B5-1:
// «класть его сюда авансом значило бы объявить перенесённым то, что не переносилось»). Отменить
// это утверждение правкой того же файла — значит потерять след решения; вместо этого корпус
// приезжает своим модулем, вместе со своим тикетом, и `deckCopy.js` остаётся честным.
//
// ⚠️ РАЗМЕТКА `<b>` РАЗОБРАНА НА ДВЕ ЧАСТИ, А НЕ ХРАНИТСЯ СТРОКОЙ HTML. В борде определение — это
// `innerHTML`; у нас тот же текст обязан стать двумя узлами, иначе пришлось бы звать
// `dangerouslySetInnerHTML` на копи из внешнего каталога. Склейка `'<b>'+lead+'</b> '+body`
// сверяется с байтом борда — то есть разбор не «примерно тот же текст», а обратимая операция.

/**
 * Семь определений (борд `:374–380`). `lead` — жирная часть внутри `<b>`, `body` — остаток.
 * Разделитель между ними у борда во всех семи один и тот же — ОДИН пробел; это утверждение стоит
 * в тесте, а не в комментарии, потому что на нём держится обратимость склейки.
 *
 * ⚠️ У `Wanting` ЖИРНАЯ ЧАСТЬ НЕ КОНЧАЕТСЯ ТОЧКОЙ, и остаток начинается с тире. Это единственная
 * запись такой формы; выравнивать её «под остальные шесть» нельзя — это цитата каталога.
 */
export const LAYER_GLOSS = [
  {
    key: 'Vitality',
    lead: 'vitality is the daily energy you have to live on.',
    body: 'the body issues a fresh supply each morning — you can’t save it, you can’t borrow tomorrow’s. on a full day, you handle the day without thinking about it. on a drained day, even small things start to cost you.',
  },
  {
    key: 'Security',
    lead: 'security is whether the ground under you feels like it will hold.',
    body: 'on solid ground, you move through the day with your guard down — you rest easy, you take risks, you can be still. hung by a thread, you brace — sleep goes thin, small surprises read as threats, rest stops being possible.',
  },
  {
    key: 'Growth',
    lead: 'growth is when the complexity of what you can handle with ease keeps increasing.',
    body: 'you take on new skills, see situations from angles you couldn’t before, find yourself in conversations you couldn’t have had a year ago. without growth, you’re sliding back — the challenges keep growing, and you don’t.',
  },
  {
    key: 'Authorship',
    lead: 'authorship is an act.',
    body: 'you make a choice and you own it. not a reaction to circumstances, not autopilot — your decision. you go after what you want, and that’s how you become the author of your own life.',
  },
  {
    key: 'Wanting',
    lead: 'wanting is the live pull toward something specific',
    body: '— something you can name, that you actually want, not just what makes sense. when it’s there, “what do you want” gets a real answer. when it’s gone, you keep moving — but you couldn’t name what you’re moving toward.',
  },
  {
    key: 'Yourself',
    lead: 'being yourself is a state.',
    body: 'it means living from your internal standard — what you value, what you believe, where your line is. no one else gets to tell you who you should be.',
  },
  {
    key: 'Lasting',
    lead: 'wanted to last is whether you wanted the experience to keep going.',
    body: 'when you do, you’re in the middle of something and you don’t want it to end — you’d live it again if you could. when you don’t, you’re in the middle of something you don’t want to keep going — you wouldn’t live it again if you could.',
  },
];

/** Определение по ключу слоя. `null`, если такого слоя нет — молчаливый `undefined` зеленит тест. */
export const glossOf = (key) => LAYER_GLOSS.find((g) => g.key === key) || null;

/**
 * Надпись над заголовком листа (борд `:638`: `slider · ${li+1} of 7`).
 *
 * Номер приходит человеческим (с единицы), а число семь — из длины корпуса слоёв, а не литералом:
 * второй дом числа семь разъехался бы с `LAYERS` контракта молча.
 */
export const sliderKicker = (index, total) => `slider · ${index + 1} of ${total}`;

/**
 * Надпись над заголовком листа ГРУППЫ (борд `sheetGroup`, `:641`: `choice · ${gi+1} of 4`). B5-4.
 *
 * Форма та же, что у `sliderKicker`, но склеивать их в одну функцию с параметром-словом нельзя:
 * у борда это две разные строки в двух разных сборках листа, и общая функция объявила бы их
 * одной. Число четыре приходит из длины корпуса групп, а не литералом.
 */
export const groupKicker = (index, total) => `choice · ${index + 1} of ${total}`;

/** Три подписи полюсов в листе (борд `:639`). У дорожки они другие — там копи самих полюсов. */
export const SHEET_POLES = ['left', 'middle', 'right'];

/**
 * Подпись единственного ряда в листе группы (борд `:642`: `<b>pick</b>`). B5-4.
 *
 * Лист группы переиспользует ту же решётку `poles2`, что лист слоя, но ряд в ней один и подпись
 * у него своя. Текст справа от подписи собирает `pickLine` из потолка КОНТРАКТА (`deckCopy.js`),
 * поэтому второй копии числа здесь нет.
 */
export const SHEET_PICK = 'pick';

/** Кнопка закрытия листа (борд `openSheet`, `:632`). Одна на оба листа — слоя и группы. */
export const SHEET_CLOSE = 'got it';
